(() => {
  'use strict';

  const config = {
    desktop: true,
    mobile: true,
    interval: 1000,
    resetOnLeave: true,
    ...(window.DEARORE_MEDIA_CONFIG || {}),
  };
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = navigator.connection?.saveData === true;
  const selector = '[data-dearore-auto-gallery], main[data-template^="product"] media-gallery';
  const timers = new WeakMap();
  const activeGalleries = new WeakSet();

  const finePointer = () => matchMedia('(hover:hover) and (pointer:fine)').matches;
  const coarsePointer = () => matchMedia('(hover:none), (pointer:coarse)').matches;
  const isProductDetail = (gallery) => Boolean(gallery.closest('main[data-template^="product"]'));
  const customSlides = (gallery) => [...gallery.querySelectorAll(':scope > [data-dearore-card-media], :scope > a > [data-dearore-card-media]')];
  const slideshowFor = (gallery) => gallery.matches('media-gallery') ? gallery.slideshow : gallery.querySelector('slideshow-component');
  const slideshowSlides = (slideshow) => [...(slideshow?.refs?.slides || slideshow?.slides || [])];

  function activeElement(gallery) {
    const custom = customSlides(gallery);
    if (custom.length) return custom.find((slide) => slide.dataset.active === 'true') || custom[0];
    const slideshow = slideshowFor(gallery);
    const slides = slideshowSlides(slideshow);
    return slides[Number(slideshow?.current || 0)] || slides[0] || gallery;
  }

  function ensureVideo(element) {
    const deferred = element?.querySelector?.('deferred-media');
    if (!deferred || deferred.querySelector('video, iframe')) return;
    if (typeof deferred.loadContent === 'function') deferred.loadContent(false);
    else if (typeof deferred.showDeferredMedia === 'function') deferred.showDeferredMedia();
  }

  function playVideo(element) {
    if (!element || reduceMotion || saveData) return;
    ensureVideo(element);
    window.setTimeout(() => {
      element.querySelectorAll?.('video').forEach((video) => {
        video.muted = true;
        video.playsInline = true;
        video.loop = true;
        video.play().catch(() => {});
      });
      element.classList?.add('is-playing');
    }, 40);
  }

  function stopVideo(element, reset = false) {
    element?.querySelectorAll?.('video').forEach((video) => {
      video.pause();
      if (reset) {
        try { video.currentTime = 0; } catch { /* Video metadata may not be ready. */ }
      }
    });
    element?.classList?.remove('is-playing');
  }

  function overlayVideo(gallery) {
    return gallery.querySelector(':scope > [data-dearore-hover-video], :scope > a > [data-dearore-hover-video]');
  }

  function selectCustom(gallery, index) {
    const slides = customSlides(gallery);
    if (!slides.length) return;
    const next = ((index % slides.length) + slides.length) % slides.length;
    slides.forEach((slide, itemIndex) => {
      const selected = itemIndex === next;
      slide.dataset.active = String(selected);
      slide.toggleAttribute('hidden', !selected);
    });
    gallery.dataset.dearoreIndex = String(next);
  }

  function selectFirst(gallery) {
    if (customSlides(gallery).length) selectCustom(gallery, 0);
    else slideshowFor(gallery)?.select?.(0, undefined, { animate: false });
  }

  function advanceCard(gallery) {
    const custom = customSlides(gallery);
    if (custom.length > 1) {
      stopVideo(activeElement(gallery), true);
      selectCustom(gallery, Number(gallery.dataset.dearoreIndex || 0) + 1);
      playVideo(activeElement(gallery));
      return;
    }
    const slideshow = slideshowFor(gallery);
    const slides = slideshowSlides(slideshow);
    if (!slideshow || slides.length < 2 || typeof slideshow.select !== 'function') return;
    stopVideo(activeElement(gallery), true);
    const current = Number(slideshow.current || 0);
    slideshow.select(current >= slides.length - 1 ? 0 : current + 1, undefined, { animate: true });
    window.setTimeout(() => playVideo(activeElement(gallery)), 80);
  }

  function start(gallery) {
    if (!gallery || activeGalleries.has(gallery) || reduceMotion || saveData) return;
    activeGalleries.add(gallery);
    gallery.dataset.dearoreAutoPlaying = 'true';

    if (isProductDetail(gallery)) {
      playVideo(activeElement(gallery));
      return;
    }

    const overlay = overlayVideo(gallery);
    if (overlay) {
      playVideo(overlay);
      return;
    }

    playVideo(activeElement(gallery));
    const slideCount = Math.max(customSlides(gallery).length, slideshowSlides(slideshowFor(gallery)).length);
    if (slideCount < 2) return;
    const interval = Math.max(800, Math.min(6000, Number(config.interval) || 1000));
    timers.set(gallery, window.setInterval(() => advanceCard(gallery), interval));
  }

  function stop(gallery, reset = false) {
    if (!gallery) return;
    window.clearInterval(timers.get(gallery));
    timers.delete(gallery);
    activeGalleries.delete(gallery);
    stopVideo(gallery, reset);
    delete gallery.dataset.dearoreAutoPlaying;
    if (reset) selectFirst(gallery);
  }

  document.addEventListener('pointerover', (event) => {
    if (!config.desktop || !finePointer()) return;
    const gallery = event.target.closest?.(selector);
    if (!gallery || gallery.contains(event.relatedTarget)) return;
    start(gallery);
  });

  document.addEventListener('pointerout', (event) => {
    if (!finePointer()) return;
    const gallery = event.target.closest?.(selector);
    if (!gallery || gallery.contains(event.relatedTarget)) return;
    stop(gallery, config.resetOnLeave !== false);
  });

  document.addEventListener('slideshow:select', (event) => {
    const gallery = event.target.closest?.('media-gallery');
    if (!gallery || !isProductDetail(gallery) || !activeGalleries.has(gallery)) return;
    stopVideo(gallery, true);
    window.setTimeout(() => playVideo(activeElement(gallery)), 60);
  });

  document.addEventListener('click', (event) => {
    if (!event.isTrusted && event.target instanceof Element && event.target.closest('[data-dearore-auto-gallery]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  const observer = new IntersectionObserver((entries) => {
    if (!config.mobile || !coarsePointer()) return;
    entries.forEach((entry) => {
      if (entry.isIntersecting && entry.intersectionRatio >= 0.65) start(entry.target);
      else stop(entry.target, config.resetOnLeave !== false);
    });
  }, { threshold: [0, 0.25, 0.65, 0.85] });

  function watch(root = document) {
    const candidates = root.matches?.(selector)
      ? [root, ...root.querySelectorAll(selector)]
      : [...root.querySelectorAll(selector)];
    candidates.forEach((gallery) => {
      if (gallery.dataset.dearoreMediaWatched) return;
      gallery.dataset.dearoreMediaWatched = 'true';
      if (customSlides(gallery).length) selectCustom(gallery, 0);
      observer.observe(gallery);
    });
  }

  watch();
  new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach((node) => {
    if (node instanceof Element) watch(node);
  }))).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', (event) => watch(event.target));
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) document.querySelectorAll(selector).forEach((gallery) => stop(gallery, true));
  });
})();
